import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useFocusHighlight — reusable helper that consumes the Focus deep-link
 * `?focus={id}` query param on EntityWorkspace and any other page that
 * renders records with `data-record-id="{canonical_id}"`.
 *
 *   1. Reads `?focus` from the URL.
 *   2. Waits for `readyKey` to indicate the relevant tab's data has
 *      rendered — pass any changing signal (e.g. list length + tab).
 *   3. Locates the matching element via a stable DOM attribute.
 *   4. `scrollIntoView({ behavior: "smooth", block: "center" })`.
 *   5. Adds `.hq-focus-pulse` for ~2s (CSS animation) then removes it.
 *   6. Strips `focus` from the URL via `setSearchParams(next, {replace})`
 *      — never pushes a new history entry, browser Back returns to HQ.
 *
 * Safe when the target is missing: does nothing, keeps the current tab
 * unchanged, leaves the URL as-is. No error UI.
 *
 * Auth: no backend calls. The hook only touches DOM already rendered by
 * the caller, which was itself scoped by server-side authorization —
 * members never see hidden ids.
 */
export function useFocusHighlight(readyKey) {
  const [sp, setSp] = useSearchParams();
  const focus = sp.get("focus");

  useEffect(() => {
    if (!focus) return;
    // Retry a few frames while React finishes committing children — first
    // paint sometimes lands one tick before the row is in the DOM.
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const escaped = (window.CSS && CSS.escape) ? CSS.escape(focus) : focus.replace(/["\\]/g, "\\$&");
      const el = document.querySelector(`[data-record-id="${escaped}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("hq-focus-pulse");
        window.setTimeout(() => el.classList.remove("hq-focus-pulse"), 2200);
        // Strip ?focus without polluting history so browser Back returns
        // straight to HQ, not to the same tab with the param re-applied.
        const next = new URLSearchParams(sp);
        next.delete("focus");
        setSp(next, { replace: true });
        return;
      }
      if (tries++ < 10) window.setTimeout(tick, 80);
    };
    // Small initial delay lets any tab-mount animation settle first.
    const t = window.setTimeout(tick, 60);
    return () => { cancelled = true; window.clearTimeout(t); };
    // readyKey is the caller's opt-in signal that the data underlying the
    // current tab has committed to DOM — re-run when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, readyKey]);
}
